/**
 * Module: `export/render-pdf.ts` — ResponseExportDocument → PDF Blob (pdfmake).
 * Visual reference for the Word export.
 */

import {
  EXPORT_BODY_FONT_SIZE_PT,
  EXPORT_CODE_FONT_SIZE_PT,
  EXPORT_COLOR_BODY,
  EXPORT_COLOR_CODE,
  EXPORT_COLOR_CODE_BG,
  EXPORT_COLOR_H1,
  EXPORT_COLOR_H2,
  EXPORT_COLOR_H3,
  EXPORT_COLOR_LINK,
  EXPORT_H1_FONT_SIZE_PT,
  EXPORT_H2_FONT_SIZE_PT,
  EXPORT_H3_FONT_SIZE_PT,
  EXPORT_LINE_HEIGHT,
  EXPORT_PAGE_MARGINS_PT,
  EXPORT_SANS_FONT_PDF,
  exportColorCss,
} from "@/lib/export/export-styles";
import { toExportMarkdown } from "@/lib/export/export-markdown";
import { markdownToPdfMakeContent } from "@/lib/export/markdown-to-pdfmake";
import { ensurePdfEmojiFont } from "@/lib/export/pdf-emoji";
import type { ResponseExportDocument } from "@/lib/export/response-export-document";

type PdfMakeFactory = {
  addVirtualFileSystem: (vfs: Record<string, string>) => void;
  addFonts?: (fonts: Record<string, Record<string, string>>) => void;
  fonts?: Record<string, Record<string, string>>;
  createPdf: (docDefinition: Record<string, unknown>) => {
    getBlob: () => Promise<Blob>;
  };
};

let pdfMakeReady: Promise<PdfMakeFactory> | null = null;

async function loadPdfMake(): Promise<PdfMakeFactory> {
  if (!pdfMakeReady) {
    pdfMakeReady = (async () => {
      const pdfMakeMod = await import("pdfmake/build/pdfmake.js");
      const fontsMod = await import("pdfmake/build/vfs_fonts.js");
      const pdfMake = (pdfMakeMod.default ?? pdfMakeMod) as PdfMakeFactory;
      const vfs = (fontsMod.default ?? fontsMod) as Record<string, string>;
      pdfMake.addVirtualFileSystem(vfs);
      await ensurePdfEmojiFont(pdfMake);
      return pdfMake;
    })();
  }
  return pdfMakeReady;
}

export async function renderExportToPdf(
  doc: ResponseExportDocument
): Promise<Blob> {
  const pdfMake = await loadPdfMake();
  const markdown = toExportMarkdown(doc);
  const content = markdownToPdfMakeContent(markdown);

  const definition = {
    info: {
      title: doc.title,
      creator: "SidonIAssist",
    },
    content,
    defaultStyle: {
      font: EXPORT_SANS_FONT_PDF,
      fontSize: EXPORT_BODY_FONT_SIZE_PT,
      lineHeight: EXPORT_LINE_HEIGHT,
      color: exportColorCss(EXPORT_COLOR_BODY),
    },
    styles: {
      h1: {
        fontSize: EXPORT_H1_FONT_SIZE_PT,
        bold: true,
        color: exportColorCss(EXPORT_COLOR_H1),
      },
      h2: {
        fontSize: EXPORT_H2_FONT_SIZE_PT,
        bold: true,
        color: exportColorCss(EXPORT_COLOR_H2),
      },
      h3: {
        fontSize: EXPORT_H3_FONT_SIZE_PT,
        bold: true,
        color: exportColorCss(EXPORT_COLOR_H3),
      },
      link: {
        color: exportColorCss(EXPORT_COLOR_LINK),
        decoration: "underline",
      },
      code: {
        fontSize: EXPORT_CODE_FONT_SIZE_PT,
        color: exportColorCss(EXPORT_COLOR_CODE),
        background: exportColorCss(EXPORT_COLOR_CODE_BG),
      },
    },
    pageMargins: [
      EXPORT_PAGE_MARGINS_PT.left,
      EXPORT_PAGE_MARGINS_PT.top,
      EXPORT_PAGE_MARGINS_PT.right,
      EXPORT_PAGE_MARGINS_PT.bottom,
    ],
  };

  return pdfMake.createPdf(definition).getBlob();
}
